import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Play,
  Square,
  RotateCcw,
  Plus,
  Trash2,
  Copy,
  Grid2X2,
  Undo2,
  Redo2,
  ClipboardCheck,
  Share2,
  Frame,
  Group,
  ImagePlus,
  Ungroup,
} from "lucide-react";
import { api, json } from "../api";
import { Modal } from "../components";
import { ResourceStatus } from "../ResourceStatus";
import { useResource } from "../useResource";
import { GameRuntime, parameter, sceneErrors } from "./runtime";
import { objectDefaults } from "./defaults";
import { LogicPanel } from "./LogicPanel";
import { LayersPanel } from "./LayersPanel";
import { StatesEditor } from "./StatesEditor";
import { AnimationsEditor } from "./AnimationsEditor";
import { poseOf, sample } from "./animation";
import { GamePlayer } from "./GamePlayer";
import { ShareDialog } from "./ShareDialog";
import { imageOf } from "./states";
import { SceneCanvas, type CanvasHandle } from "./canvas/SceneCanvas";
import * as tree from "./tree";
import type { EditorHandle } from "../DocumentEditor";
import type { Asset, Entity } from "../types";
import type { Animation, GameObject, Scene, SceneContent } from "./types";
type Props = {
  initial: Scene;
  scenes: Scene[];
  entities: Entity[];
  assets: Asset[];
  readOnly: boolean;
  onSaved: (s: Scene) => void;
  onSelect: (s: Scene) => void;
  onCreate: (demo: boolean) => void;
  onReload: () => Promise<void>;
  onCreatePlaytest?: (scene: Scene) => void;
  onAsset?: (asset: Asset) => void;
};
const kindNames = {
  rect: "Прямоугольник",
  circle: "Круг",
  image: "Изображение",
  text: "Текст",
  group: "Группа",
  frame: "Фрейм",
};
export const SceneEditor = forwardRef<EditorHandle, Props>(
  function SceneEditor(props, ref) {
    const { initial, readOnly, entities, assets } = props,
      resource = useResource(
        useMemo(
          () => ({ ...initial, content: tree.normalize(initial.content) }),
          [initial],
        ),
        `/projects/${initial.projectId}/scenes/${initial.id}`,
        props.onSaved,
      ),
      { data, change, flush } = resource;
    const [selected, setSelected] = useState<string[]>(() => {
        const player = initial.content.objects.find((o) => o.player);
        return player ? [player.id] : [];
      }),
      [running, setRunning] = useState(false),
      [runId, setRunId] = useState(0),
      [, setFrame] = useState(0),
      [grid, setGrid] = useState(true),
      [error, setError] = useState(""),
      [uploading, setUploading] = useState(0),
      [trash, setTrash] = useState(false),
      [sharing, setSharing] = useState(false),
      [preview, setPreview] = useState<{
        id: string;
        animation: Animation;
      } | null>(null),
      [previewObject, setPreviewObject] = useState<GameObject | null>(null);
    const canvas = useRef<CanvasHandle>(null),
      files = useRef<HTMLInputElement>(null),
      game = useRef<GameRuntime | null>(null),
      past = useRef<SceneContent[]>([]),
      future = useRef<SceneContent[]>([]);
    useImperativeHandle(ref, () => ({ flush }), [flush]);
    // Preview plays an animation on the canvas without touching the scene.
    useEffect(() => {
      if (!preview) {
        setPreviewObject(null);
        return;
      }
      const base = resource
        .getCurrent()
        .content.objects.find((o) => o.id === preview.id);
      if (!base) return setPreview(null);
      const start = poseOf(base),
        began = performance.now();
      let handle = 0,
        stop = 0;
      const tick = (t: number) => {
        const { pose, done } = sample(
          base,
          preview.animation,
          start,
          start,
          (t - began) / 1000,
        );
        setPreviewObject({ ...base, ...pose, visible: true, opacity: pose.visible ? pose.opacity : pose.opacity * 0.25 });
        if (done) stop = window.setTimeout(() => setPreview(null), 600);
        else handle = requestAnimationFrame(tick);
      };
      handle = requestAnimationFrame(tick);
      return () => {
        cancelAnimationFrame(handle);
        clearTimeout(stop);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview]);
    useEffect(() => {
      if (running) setPreview(null);
    }, [running]);
    const canvasObjects = useMemo(
      () =>
        previewObject
          ? data.content.objects.map((o) =>
              o.id === previewObject.id ? previewObject : o,
            )
          : data.content.objects,
      [data.content.objects, previewObject],
    );
    const displayed = running && game.current ? game.current.scene : data,
      objects =
        running && game.current ? game.current.objects : data.content.objects,
      object =
        selected.length === 1
          ? data.content.objects.find((o) => o.id === selected[0])
          : undefined,
      locked = readOnly || running,
      errors = sceneErrors(data, props.scenes, entities);
    const variables = data.content.variables,
      resolvers = useMemo(
        () => ({
          imageOf: (o: GameObject) => imageOf(o, entities),
          animated: (asset: string) => {
            const mime = assets.find((a) => a.id === asset)?.mime;
            return mime === "image/gif" || mime === "image/webp";
          },
          textOf: (o: GameObject) =>
            o.text.replace(/\{([\p{L}\p{N}_]+)\}/gu, (_m, key) =>
              String(variables.find((v) => v.name === key)?.value ?? `{${key}}`),
            ),
        }),
        [entities, variables, assets],
      );
    function content(next: SceneContent, record = true) {
      if (locked) return;
      if (record) {
        past.current = [
          ...past.current.slice(-49),
          structuredClone(data.content),
        ];
        future.current = [];
      }
      change({ content: { ...next, objects: tree.fitGroups(next.objects) } });
    }
    const setObjects = (next: GameObject[]) =>
      content({ ...data.content, objects: next });
    function patchOne(id: string, values: Partial<GameObject>) {
      setObjects(
        data.content.objects.map((o) => (o.id === id ? { ...o, ...values } : o)),
      );
    }
    function patch(values: Partial<GameObject>) {
      if (object) patchOne(object.id, values);
    }
    function patchLatest(
      id: string,
      change: (o: GameObject) => Partial<GameObject>,
    ) {
      const current = resource.getCurrent().content;
      content({
        ...current,
        objects: current.objects.map((o) =>
          o.id === id ? { ...o, ...change(o) } : o,
        ),
      });
    }
    async function uploadAsset(file: File) {
      if (file.size > 20 * 1024 * 1024)
        throw new Error(`«${file.name}» больше 20 МБ.`);
      const body = new FormData();
      body.append("image", file);
      const asset = await api<Asset & { width?: number; height?: number }>(
        `/projects/${initial.projectId}/assets`,
        { method: "POST", body },
      );
      props.onAsset?.(asset);
      return asset;
    }
    function geometry(
      key: "x" | "y" | "width" | "height" | "rotation",
      value: number,
    ) {
      if (!object || !Number.isFinite(value)) return;
      if (object.kind === "group" && (key === "x" || key === "y")) {
        setObjects(
          tree.translate(
            data.content.objects,
            [object.id],
            key === "x" ? value - object.x : 0,
            key === "y" ? value - object.y : 0,
          ),
        );
        return;
      }
      if (object.kind === "frame" && (key === "x" || key === "y")) {
        // Moving a frame carries its contents along.
        setObjects(
          tree.translate(
            data.content.objects,
            [object.id],
            key === "x" ? value - object.x : 0,
            key === "y" ? value - object.y : 0,
          ),
        );
        return;
      }
      const limit = key === "rotation" ? 360 : 1000000,
        v =
          key === "width" || key === "height"
            ? Math.max(1, Math.min(limit, value))
            : Math.max(-limit, Math.min(limit, value));
      patch({ [key]: v });
    }
    const centre = () => canvas.current?.centre() || { x: 100, y: 100 };
    function place(o: GameObject) {
      const c = centre();
      o.x = Math.round(c.x - o.width / 2);
      o.y = Math.round(c.y - o.height / 2);
      return o;
    }
    function add(kind: GameObject["kind"], entityId: string | null = null) {
      const e = entities.find((e) => e.id === entityId),
        o = {
          ...objectDefaults(e?.title || kindNames[kind]),
          kind,
          entityId,
        };
      if (kind === "frame") {
        o.width = 480;
        o.height = 320;
        o.color = "gray";
      }
      if (e) {
        o.imageId = null;
        const speed = e.properties.find(
          (p) => p.type === "number" && /^(скорость|speed)$/i.test(p.name),
        );
        o.bindings.speed = speed?.id || null;
      }
      place(o);
      setObjects(
        tree.reparentToFrames([...data.content.objects, o], [o.id]),
      );
      setSelected([o.id]);
    }
    async function upload(list: File[], at?: { x: number; y: number }) {
      if (locked || !list.length) return;
      setError("");
      setUploading(list.length);
      const added: GameObject[] = [];
      let x = at?.x ?? centre().x,
        y = at?.y ?? centre().y;
      try {
        for (const file of list) {
          const asset = await uploadAsset(file);
          const o = {
            ...objectDefaults(file.name.replace(/\.[^.]+$/, "").slice(0, 160) || "Изображение"),
            kind: "image" as const,
            imageId: asset.id,
            width: asset.width || 256,
            height: asset.height || 256,
          };
          o.x = Math.round(x);
          o.y = Math.round(y);
          x += o.width + 24;
          added.push(o);
          setUploading((n) => n - 1);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setUploading(0);
        if (added.length) {
          const current = resource.getCurrent().content;
          content({
            ...current,
            objects: tree.reparentToFrames(
              [...current.objects, ...added],
              added.map((o) => o.id),
            ),
          });
          setSelected(added.map((o) => o.id));
        }
      }
    }
    function duplicate() {
      if (!selected.length) return;
      const result = tree.duplicate(data.content.objects, selected);
      setObjects(result.objects);
      setSelected(result.ids);
    }
    function remove() {
      if (!selected.length) return;
      setObjects(tree.remove(data.content.objects, selected));
      setSelected([]);
    }
    function group() {
      const result = tree.group(data.content.objects, selected);
      if (!result) return;
      setObjects(result.objects);
      setSelected([result.id]);
    }
    function ungroup() {
      const result = tree.ungroup(data.content.objects, selected);
      if (!result) return;
      setObjects(result.objects);
      setSelected(result.ids);
    }
    function undo(redo = false) {
      const from = redo ? future.current : past.current,
        to = redo ? past.current : future.current;
      if (!from.length || locked) return;
      to.push(structuredClone(data.content));
      change({ content: from.pop()! });
      setSelected([]);
    }
    function commit(next: GameObject[], moved: string[]) {
      setObjects(moved.length ? tree.reparentToFrames(next, moved) : next);
    }
    function editorKeys(e: React.KeyboardEvent) {
      if (locked) return;
      const mod = e.ctrlKey || e.metaKey,
        key = e.key.toLowerCase();
      if (e.key === "Delete" || e.key === "Backspace") remove();
      else if (mod && key === "d") duplicate();
      else if (mod && (key === "z" || key === "y"))
        undo(e.shiftKey || key === "y");
      else if (mod && key === "g") e.shiftKey ? ungroup() : group();
      else if (mod && key === "a")
        setSelected(
          data.content.objects.filter((o) => !o.parentId).map((o) => o.id),
        );
      else if (e.key === "Escape") {
        const parent = object?.parentId;
        setSelected(parent ? [parent] : []);
      } else if (e.shiftKey && e.code === "Digit2")
        canvas.current?.focus(selected);
      else if (e.key.startsWith("Arrow") && selected.length) {
        const step = e.shiftKey ? 10 : 1,
          dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0,
          dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        const ids = tree
          .topmost(data.content.objects, selected)
          .filter((o) => !o.locked)
          .map((o) => o.id);
        setObjects(tree.translate(data.content.objects, ids, dx, dy));
      } else return;
      e.preventDefault();
    }
    async function start() {
      if (!(await flush())) return;
      const snapshot = resource.getCurrent(),
        list = props.scenes.map((s) => (s.id === snapshot.id ? snapshot : s));
      const errors = sceneErrors(snapshot, list, entities);
      if (errors.length) {
        setError(errors.join(" "));
        return;
      }
      game.current = new GameRuntime(list, entities, snapshot.id);
      setRunning(true);
      setRunId((v) => v + 1);
      setError("");
    }
    function stop() {
      setRunning(false);
      game.current = null;
      setFrame((v) => v + 1);
    }
    function sceneSize(key: "width" | "height", value: number) {
      if (!Number.isFinite(value)) return;
      const size = Math.round(
        Math.max(key === "width" ? 320 : 240, Math.min(4096, value)),
      );
      content({ ...data.content, [key]: size });
    }
    return (
      <div className="scene-editor">
        <div className="scene-top">
          <select
            aria-label="Выбор сцены"
            disabled={running}
            value={data.id}
            onChange={(e) => {
              const s = props.scenes.find((s) => s.id === e.target.value);
              if (s) props.onSelect(s);
            }}
          >
            {props.scenes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id === data.id ? data.title : s.title}
              </option>
            ))}
          </select>
          <button
            className="text-button"
            disabled={locked}
            onClick={() => props.onCreate(false)}
          >
            <Plus size={14} />
            Сцена
          </button>
          <button
            className="text-button"
            disabled={locked}
            onClick={() => props.onCreate(true)}
          >
            Пример с кристаллами
          </button>
          <ResourceStatus resource={resource} readOnly={readOnly} />
          {running ? (
            <>
              <button className="secondary" onClick={() => void start()}>
                <RotateCcw size={14} />
                Заново
              </button>
              <button className="primary" onClick={stop}>
                <Square size={14} />
                Остановить
              </button>
            </>
          ) : (
            <>
              {props.onCreatePlaytest && !readOnly && (
                <button
                  className="secondary"
                  disabled={errors.length > 0}
                  onClick={async () => {
                    if (await flush())
                      props.onCreatePlaytest?.(resource.getCurrent());
                  }}
                >
                  <ClipboardCheck size={15} />
                  Плейтест
                </button>
              )}
              <button
                className="secondary"
                disabled={errors.length > 0}
                title="Ссылка, по которой прототип можно открыть без аккаунта"
                onClick={() => setSharing(true)}
              >
                <Share2 size={15} />
                Поделиться
              </button>
              <button
                className="primary"
                disabled={errors.length > 0}
                onClick={() => void start()}
              >
                <Play size={15} />
                Играть
              </button>
            </>
          )}
        </div>
        {sharing && (
          <ShareDialog
            projectId={initial.projectId}
            scene={data}
            scenes={props.scenes}
            readOnly={readOnly}
            flush={flush}
            close={() => setSharing(false)}
          />
        )}
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        {errors.length > 0 && !running && (
          <div className="scene-validation" role="alert">
            <strong>Перед запуском:</strong>
            {errors.map((e, i) => (
              <span key={i}>{e}</span>
            ))}
          </div>
        )}
        <div className={`scene-layout ${running ? "is-running" : ""}`}>
          {!running && (
            <LayersPanel
              objects={data.content.objects}
              selected={selected}
              readOnly={locked}
              onSelect={setSelected}
              onPatch={patchOne}
              onMove={(ids, target, place) =>
                setObjects(tree.move(data.content.objects, ids, target, place))
              }
            />
          )}
          <div className="scene-center">
            <div className="scene-toolbar">
              {(["rect", "circle", "image", "text", "frame"] as const).map(
                (kind) => (
                  <button
                    className="text-button"
                    key={kind}
                    disabled={locked}
                    onClick={() => add(kind)}
                  >
                    {kind === "frame" ? <Frame size={12} /> : <Plus size={12} />}
                    {kindNames[kind]}
                  </button>
                ),
              )}
              <select
                aria-label="Добавить сущность в сцену"
                disabled={locked}
                value=""
                onChange={(e) => {
                  if (e.target.value)
                    add(
                      entities.find((v) => v.id === e.target.value)?.imageId
                        ? "image"
                        : "rect",
                      e.target.value,
                    );
                }}
              >
                <option value="">+ Сущность</option>
                {entities.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </select>
              <input
                ref={files}
                type="file"
                className="file-input"
                multiple
                accept="image/png,image/jpeg,image/webp,image/gif"
                aria-label="Загрузить картинки на холст"
                onChange={(e) => {
                  void upload([...(e.target.files || [])]);
                  e.target.value = "";
                }}
              />
              <button
                className="text-button"
                disabled={locked || uploading > 0}
                onClick={() => files.current?.click()}
              >
                <ImagePlus size={13} />
                {uploading ? `Загрузка… ${uploading}` : "Картинки"}
              </button>
              <button
                className="icon-button"
                aria-label="Сгруппировать"
                title="Ctrl+G"
                disabled={locked || !selected.length}
                onClick={group}
              >
                <Group size={15} />
              </button>
              <button
                className="icon-button"
                aria-label="Разгруппировать"
                title="Ctrl+Shift+G"
                disabled={
                  locked ||
                  !data.content.objects.some(
                    (o) => selected.includes(o.id) && tree.isContainer(o),
                  )
                }
                onClick={ungroup}
              >
                <Ungroup size={15} />
              </button>
              <button
                className="icon-button"
                aria-label="Сетка сцены"
                aria-pressed={grid}
                onClick={() => setGrid(!grid)}
              >
                <Grid2X2 size={15} />
              </button>
              <button
                className="icon-button"
                aria-label="Отменить в сцене"
                disabled={locked || !past.current.length}
                onClick={() => undo()}
              >
                <Undo2 size={15} />
              </button>
              <button
                className="icon-button"
                aria-label="Повторить в сцене"
                disabled={locked || !future.current.length}
                onClick={() => undo(true)}
              >
                <Redo2 size={15} />
              </button>
            </div>
            {running ? (
              <GamePlayer
                key={runId}
                runtime={game.current!}
                entities={entities}
                assetUrl={(id) => `/api/projects/${initial.projectId}/assets/${id}`}
                onRestart={() => void start()}
                onFrame={() => setFrame((v) => v + 1)}
              />
            ) : (
              <>
                <SceneCanvas
                  ref={canvas}
                  projectId={initial.projectId}
                  objects={canvasObjects}
                  width={data.content.width}
                  height={data.content.height}
                  grid={grid}
                  selected={selected}
                  readOnly={locked || !!preview}
                  resolvers={resolvers}
                  onSelect={setSelected}
                  onCommit={commit}
                  onOpen={() => undefined}
                  onKeyDown={editorKeys}
                  onFiles={(list, at) => void upload(list, at)}
                />
                <div className="scene-underbar">
                  <span>
                    Игровая область {data.content.width} ×{" "}
                    {data.content.height} · колесо: прокрутка, Ctrl+колесо:
                    масштаб, пробел + мышь: перемещение · картинки можно
                    перетащить на холст
                  </span>
                </div>
              </>
            )}
            <LogicPanel
              content={data.content}
              change={content}
              readOnly={locked}
              scenes={props.scenes}
              live={running ? game.current?.variables || {} : null}
              logs={game.current?.log || []}
            />
          </div>
          <aside className="scene-inspector">
            <label>
              Название сцены
              <input
                aria-label="Название сцены"
                value={data.title}
                maxLength={160}
                disabled={locked}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>
            <div className="board-properties">
              {(["width", "height"] as const).map((key) => (
                <label key={key}>
                  {key === "width" ? "Ширина сцены" : "Высота сцены"}
                  <input
                    type="number"
                    disabled={locked}
                    value={data.content[key]}
                    onChange={(e) => sceneSize(key, Number(e.target.value))}
                  />
                </label>
              ))}
            </div>
            {selected.length > 1 && (
              <div className="scene-multi">
                <span className="eyebrow">ВЫБРАНО: {selected.length}</span>
                <div className="entity-inspector-actions">
                  <button className="text-button" disabled={locked} onClick={group}>
                    <Group size={14} />
                    Сгруппировать
                  </button>
                  <button className="text-button" disabled={locked} onClick={duplicate}>
                    <Copy size={14} />
                    Копия
                  </button>
                  <button
                    className="icon-button"
                    aria-label="Удалить выбранные объекты"
                    disabled={locked}
                    onClick={remove}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
            {object && (
              <>
                <div className="entity-inspector-actions">
                  <button
                    className="text-button"
                    disabled={locked}
                    onClick={duplicate}
                  >
                    <Copy size={14} />
                    Копия
                  </button>
                  <button
                    className="icon-button"
                    aria-label="Удалить объект сцены"
                    disabled={locked}
                    onClick={remove}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <label>
                  Название объекта
                  <input
                    aria-label="Название объекта"
                    value={object.name}
                    disabled={locked}
                    maxLength={160}
                    onChange={(e) => patch({ name: e.target.value })}
                  />
                </label>
                {!tree.isContainer(object) && (
                <label>
                  Сущность
                  <select
                    aria-label="Сущность объекта"
                    value={object.entityId || ""}
                    disabled={locked}
                    onChange={(e) =>
                      patch({
                        entityId: e.target.value || null,
                        bindings: { speed: null, dashDistance: null },
                      })
                    }
                  >
                    <option value="">Самостоятельный объект</option>
                    {entities.map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {entity.title}
                      </option>
                    ))}
                  </select>
                </label>
                )}
                <div className="board-properties">
                  {(["x", "y", "width", "height"] as const).map((key) => (
                    <label key={key}>
                      {
                        { x: "X", y: "Y", width: "Ширина", height: "Высота" }[
                          key
                        ]
                      }
                      <input
                        aria-label={`Объект ${key}`}
                        type="number"
                        disabled={
                          locked ||
                          (object.kind === "group" &&
                            (key === "width" || key === "height"))
                        }
                        value={Math.round(object[key])}
                        onChange={(e) => geometry(key, Number(e.target.value))}
                      />
                    </label>
                  ))}
                  {!tree.isContainer(object) && (
                    <label>
                      Поворот, °
                      <input
                        aria-label="Поворот объекта"
                        type="number"
                        step={15}
                        disabled={locked}
                        value={Math.round(object.rotation * 10) / 10}
                        onChange={(e) =>
                          geometry("rotation", Number(e.target.value))
                        }
                      />
                    </label>
                  )}
                  <label>
                    Непрозрачность, %
                    <input
                      aria-label="Непрозрачность объекта"
                      type="number"
                      min={0}
                      max={100}
                      disabled={locked}
                      value={Math.round(object.opacity * 100)}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v))
                          patch({ opacity: Math.max(0, Math.min(100, v)) / 100 });
                      }}
                    />
                  </label>
                  {!tree.isContainer(object) && (
                    <label>
                      Тень, px
                      <input
                        aria-label="Тень объекта"
                        type="number"
                        min={0}
                        max={100}
                        disabled={locked}
                        value={object.shadow}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v))
                            patch({ shadow: Math.max(0, Math.min(100, v)) });
                        }}
                      />
                    </label>
                  )}
                </div>
                {!tree.isContainer(object) && (
                <label>
                  Вид объекта
                  <select
                    value={object.kind}
                    disabled={locked}
                    onChange={(e) =>
                      patch({ kind: e.target.value as GameObject["kind"] })
                    }
                  >
                    {(["rect", "circle", "image", "text"] as const).map((key) => (
                      <option key={key} value={key}>
                        {kindNames[key]}
                      </option>
                    ))}
                  </select>
                </label>
                )}
                <label>
                  Цвет
                  <select
                    value={object.color}
                    disabled={locked}
                    onChange={(e) =>
                      patch({ color: e.target.value as GameObject["color"] })
                    }
                  >
                    {Object.entries({
                      violet: "Фиолетовый",
                      green: "Зелёный",
                      amber: "Тёплый",
                      gray: "Серый",
                      red: "Красный",
                    }).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                {object.kind === "image" && (
                  <StatesEditor
                    object={object}
                    assets={assets}
                    projectId={initial.projectId}
                    locked={locked}
                    onPatch={patch}
                    onPatchLatest={(change) => patchLatest(object.id, change)}
                    onUpload={async (file) => {
                      setError("");
                      try {
                        return await uploadAsset(file);
                      } catch (e) {
                        setError((e as Error).message);
                        return null;
                      }
                    }}
                  />
                )}
                {!tree.isContainer(object) && (
                  <AnimationsEditor
                    object={object}
                    locked={locked}
                    previewing={
                      preview?.id === object.id ? preview.animation.id : null
                    }
                    onPatch={patch}
                    onPreview={(animation) =>
                      setPreview(
                        animation ? { id: object.id, animation } : null,
                      )
                    }
                  />
                )}
                {object.kind === "text" && (
                  <label>
                    Текст · переменные в {"{скобках}"}
                    <textarea
                      rows={3}
                      disabled={locked}
                      value={object.text}
                      onChange={(e) => patch({ text: e.target.value })}
                    />
                  </label>
                )}
                {!tree.isContainer(object) && (
                <div className="scene-behaviors">
                  <span className="eyebrow">СОСТОЯНИЕ И ПОВЕДЕНИЯ</span>
                  {(
                    [
                      ["active", "Активен"],
                      ["visible", "Виден"],
                      ["player", "Управляемый персонаж"],
                      ["solid", "Твёрдое препятствие"],
                      ["pickup", "Подбираемый предмет"],
                      ["trigger", "Зона-триггер"],
                      ["interactable", "Взаимодействие · E"],
                      ["dash", "Рывок · Space"],
                    ] as const
                  ).map(([key, label]) => (
                    <label className="game-check" key={key}>
                      <input
                        type="checkbox"
                        checked={object[key]}
                        disabled={locked}
                        onChange={(e) => patch({ [key]: e.target.checked })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                )}
                {object.player && (
                  <>
                    <label>
                      Управление
                      <select
                        aria-label="Управление персонажем"
                        value={object.control}
                        disabled={locked}
                        onChange={(e) =>
                          patch({
                            control: e.target.value as GameObject["control"],
                          })
                        }
                      >
                        <option value="wasd">WASD / стрелки</option>
                        <option value="click">Клик по точке</option>
                      </select>
                    </label>
                    {(["speed", "dashDistance"] as const).map((key) => (
                      <div className="scene-parameter" key={key}>
                        <label>
                          {key === "speed"
                            ? "Скорость, ед./с"
                            : "Дальность рывка, ед."}
                          <input
                            aria-label={
                              key === "speed"
                                ? "Скорость объекта"
                                : "Дальность рывка объекта"
                            }
                            type="number"
                            min={0}
                            max={2000}
                            disabled={locked}
                            value={parameter(object, key, entities)}
                            onChange={(e) =>
                              patch({
                                [key]: Math.max(
                                  0,
                                  Math.min(2000, Number(e.target.value)),
                                ),
                              })
                            }
                          />
                        </label>
                        {object.entityId && (
                          <>
                            <select
                              aria-label={`Источник ${key}`}
                              value={object.bindings[key] || ""}
                              disabled={locked}
                              onChange={(e) =>
                                patch({
                                  bindings: {
                                    ...object.bindings,
                                    [key]: e.target.value || null,
                                  },
                                  [key]: null,
                                })
                              }
                            >
                              <option value="">Стандартное значение</option>
                              {entities
                                .find((e) => e.id === object.entityId)
                                ?.properties.filter((p) => p.type === "number")
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    Из сущности: {p.name}
                                  </option>
                                ))}
                            </select>
                            {object[key] !== null ? (
                              <button
                                className="text-button"
                                disabled={locked}
                                onClick={() => patch({ [key]: null })}
                              >
                                Сбросить переопределение
                              </button>
                            ) : (
                              <span className="small-text muted">
                                {object.bindings[key]
                                  ? "Значение из сущности"
                                  : "Стандартное значение"}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    <label>
                      Перезарядка рывка, сек.
                      <input
                        type="number"
                        min={0.1}
                        max={60}
                        step={0.1}
                        disabled={locked}
                        value={object.dashCooldown}
                        onChange={(e) =>
                          patch({
                            dashCooldown: Math.max(
                              0.1,
                              Math.min(60, Number(e.target.value)),
                            ),
                          })
                        }
                      />
                    </label>
                    <label>
                      Радиус взаимодействия
                      <input
                        type="number"
                        min={1}
                        max={2000}
                        disabled={locked}
                        value={object.interactionRadius}
                        onChange={(e) =>
                          patch({
                            interactionRadius: Math.max(
                              1,
                              Math.min(2000, Number(e.target.value)),
                            ),
                          })
                        }
                      />
                    </label>
                  </>
                )}
              </>
            )}
            <div className="inspector-group">
              <button
                className="text-button"
                disabled={locked}
                onClick={() => setTrash(true)}
              >
                <Trash2 size={14} />
                Сцену в корзину
              </button>
            </div>
          </aside>
        </div>
        {trash && (
          <Modal
            title="Переместить сцену в корзину?"
            close={() => setTrash(false)}
          >
            <p>
              Объекты и правила сохранятся в корзине. Другие сцены могут
              ссылаться на эту сцену.
            </p>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setTrash(false)}>
                Отмена
              </button>
              <button
                className="danger"
                onClick={async () => {
                  try {
                    if (!(await flush())) return;
                    await api(
                      `/projects/${initial.projectId}/scenes/${data.id}/trash`,
                      json("POST", { version: resource.getCurrent().version }),
                    );
                    setTrash(false);
                    await props.onReload();
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                В корзину
              </button>
            </div>
          </Modal>
        )}
      </div>
    );
  },
);
